import React, {ReactElement} from "react";

import {logEvent} from "/src/utils/amplitude";
import {css} from "/src/utils/utils";

import LinkTag from "../../buttons/link-tag/LinkTag";
import GDPRContext from "/src/contexts/GDPRContext";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import LanguageSelector from "../language-selector/LanguageSelector";
import useHasWindow from "/src/hooks/useHasWindow";

import * as styles from "./notification-bar.module.scss";
import SignInSelector from "../sign-in-selector/SignInSelector";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";

type NavNodes =
  Queries.ContentfulNavComponentQueryQuery["allContentfulSuperMainNavBars"]["nodes"][0];

export interface NotificationBarProps
  extends Pick<
    NonNullable<NavNodes>,
    | "alertText"
    | "promotionalText"
    | "euPromotionalText"
    | "superNavPromotionalCtaText"
    | "superNavPromotionalCtaLink"
    | "superNavPromotionalCtaButton"
    | "euSuperNavPromotionalCtaLink"
    | "euSuperNavPromotionalCtaText"
    | "euSuperNavPromotionalCtaButton"
    | "leavePromotionalTextBlankForEu"
  > {
  placeholder?: boolean;
}

// separating this as micro component to perform partial hydation
// ref: https://www.gatsbyjs.com/docs/how-to/performance/partial-hydration/#when-to-use-client-components
const PromotionalText = (props: NotificationBarProps): ReactElement | null => {
  const {
    promotionalText,
    superNavPromotionalCtaText,
    superNavPromotionalCtaLink,
    superNavPromotionalCtaButton,
    euPromotionalText,
    euSuperNavPromotionalCtaText,
    euSuperNavPromotionalCtaLink,
    euSuperNavPromotionalCtaButton,
    leavePromotionalTextBlankForEu,
    alertText,
  } = props;

  const hasWindow = useHasWindow();

  const gdpr = React.useContext(GDPRContext);

  const promotionalTextToShow = (): string => {
    if (gdpr.isEU) {
      if (euPromotionalText != null) {
        return euPromotionalText.euPromotionalText!;
      }

      return leavePromotionalTextBlankForEu != null
        ? ""
        : promotionalText?.promotionalText!;
    }
    return promotionalText?.promotionalText!;
  };

  const superNavPromotionalCtaTextToShow = (): string => {
    if (gdpr.isEU) {
      if (euSuperNavPromotionalCtaText != null) {
        return euSuperNavPromotionalCtaText;
      }

      return leavePromotionalTextBlankForEu != null
        ? ""
        : superNavPromotionalCtaText!;
    }
    return superNavPromotionalCtaText!;
  };

  const superNavPromotionalCtaLinkToShow = (): string => {
    if (gdpr.isEU) {
      if (euSuperNavPromotionalCtaLink != null) {
        return euSuperNavPromotionalCtaLink;
      }

      return leavePromotionalTextBlankForEu != null
        ? ""
        : superNavPromotionalCtaLink!;
    }
    return superNavPromotionalCtaLink!;
  };

  const superNavPromotionalCtaButtonToShow = () => {
    return gdpr.isEU && euSuperNavPromotionalCtaButton
      ? euSuperNavPromotionalCtaButton
      : superNavPromotionalCtaButton;
  };

  const ampProps = {
    name: "superNavPromotionalCta",
    type: "watch",
    linkUrl: superNavPromotionalCtaLink || "",
  };

  const handleClick = () => {
    logEvent("click:marketing_site_cta", {
      ...ampProps,
    });
  };

  return (
    <div
      className={css(
        styles.notificationBar__left__content,
        !hasWindow ? styles.notificationBar__left__contentLoading : ""
      )}
    >
      {hasWindow ? (
        <>
          <span className={styles.notificationBar__left__content__text}>
            {alertText != null && `${alertText}&nbsp;`}
          </span>
          <span>{promotionalTextToShow()}&nbsp;</span>

          {superNavPromotionalCtaButtonToShow() ? (
            <CTAButton
              cta={superNavPromotionalCtaButtonToShow() as CTA}
              small
              classes={css(styles.notificationBar__left__content__cta)}
            />
          ) : (
            <>
              {superNavPromotionalCtaText != null && (
                <LinkTag
                  href={superNavPromotionalCtaLinkToShow() || ""}
                  openInNewTab
                  onClick={handleClick}
                  classes={css(styles.notificationBar__left__content__cta)}
                >
                  {superNavPromotionalCtaTextToShow() || ""}
                </LinkTag>
              )}
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

const NotificationBar = (props: NotificationBarProps): ReactElement | null => {
  const {placeholder = false} = props;

  return (
    <div
      aria-hidden={placeholder}
      className={css(
        styles.notificationBar,
        placeholder ? styles.notificationBarPlaceholder : ""
      )}
    >
      <ModuleBody classes={styles.notificationBar__body}>
        <PromotionalText {...props} />
        <div className={styles.notificationBar__right}>
          <SignInSelector />
          <LanguageSelector />
        </div>
      </ModuleBody>
    </div>
  );
};

export default NotificationBar;
